import { useIntl } from 'react-intl';
import { Col, Input, Row, Select } from 'antd';

import moment from 'moment';
import { ExtraFiltersProps } from '@/types/ExtraFiltersProps';
import { InputDate } from '@/components/_common';
import { DateTimeFormat } from '@/utils/moment';
import {
  InvoiceOriginReferenceEntity,
  InvoiceSituation,
} from '@/core/domain/invoice/invoices/invoices.enums';
import { InvoiceListParams } from '@/core/domain/invoice/invoices/invoices.types';
import CpfCnpjInput from '@/components/_common/CpfCnpjInput';
import { removeCpfCnpjMask } from '@/utils/mask/removeMask';

function InvoiceExtraFilters({
  queryParams,
  setQueryParams,
}: ExtraFiltersProps<InvoiceListParams>) {
  const intl = useIntl();

  return (
    <Row gutter={[16, 16]}>
      <Col span={24} md={12} lg={6}>
        <Input
          value={queryParams?.number}
          onChange={({ target }) =>
            setQueryParams((prev) => ({ ...prev, number: target.value }))
          }
          placeholder={intl.formatMessage({
            id: 'invoice.domain.number',
          })}
        />
      </Col>

      <Col span={24} md={12} lg={6}>
        <Input
          value={queryParams?.serie}
          onChange={({ target: { value } }) =>
            setQueryParams((prev) => ({ ...prev, serie: value }))
          }
          placeholder={intl.formatMessage({
            id: 'invoice.domain.series',
          })}
        />
      </Col>

      <Col span={24} md={12} lg={6}>
        <InputDate
          value={
            queryParams?.emissionDate
              ? moment(queryParams?.emissionDate)
              : undefined
          }
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              emissionDate: value?.format(DateTimeFormat),
            }))
          }
          placeholder={intl.formatMessage({
            id: 'invoice.domain.emissionDate',
          })}
        />
      </Col>

      <Col span={24} md={12} lg={6}>
        <Select
          allowClear
          mode="multiple"
          style={{ width: '100%' }}
          value={queryParams?.situation}
          onChange={(value) =>
            setQueryParams((prev) => ({ ...prev, situation: value }))
          }
          options={[
            {
              value: InvoiceSituation.REPLACED,
              label: intl.formatMessage({
                id: 'invoice.nfse.filters.situation.replaced',
              }),
            },
            {
              value: InvoiceSituation.IN_TYPING,
              label: intl.formatMessage({
                id: 'invoice.nfse.filters.situation.inTyping',
              }),
            },
            {
              value: InvoiceSituation.VALIDATED,
              label: intl.formatMessage({
                id: 'invoice.nfse.filters.situation.validated',
              }),
            },
            {
              value: InvoiceSituation.PROCESSING,
              label: intl.formatMessage({
                id: 'invoice.nfse.filters.situation.processing',
              }),
            },
            {
              value: InvoiceSituation.AUTHORIZED,
              label: intl.formatMessage({
                id: 'invoice.nfse.filters.situation.authorized',
              }),
            },
            {
              value: InvoiceSituation.CANCELED,
              label: intl.formatMessage({
                id: 'invoice.nfse.filters.situation.canceled',
              }),
            },
            {
              value: InvoiceSituation.DENIED,
              label: intl.formatMessage({
                id: 'invoice.nfse.filters.situation.denied',
              }),
            },
            {
              value: InvoiceSituation.INTERRUPTED,
              label: intl.formatMessage({
                id: 'invoice.nfse.filters.situation.interrupted',
              }),
            },
          ]}
          placeholder={intl.formatMessage({ id: 'invoice.domain.situation' })}
        />
      </Col>

      <Col span={24} md={12} lg={6}>
        <Input
          placeholder="Destinatário"
          value={queryParams?.description}
          onChange={({ target }) =>
            setQueryParams((prev) => ({
              ...prev,
              description: target.value,
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={6}>
        <Select
          options={[
            { value: InvoiceOriginReferenceEntity.PURCHASE, label: 'Compra' },
            { value: InvoiceOriginReferenceEntity.SALES, label: 'Venda' },
            {
              value: InvoiceOriginReferenceEntity.WORK_ORDER,
              label: 'Orçamento',
            },
          ]}
          style={{ width: '100%' }}
          placeholder="Tipo de Origem"
          value={queryParams?.entity}
          onChange={(value: InvoiceOriginReferenceEntity) =>
            setQueryParams((prev) => ({
              ...prev,
              entity: value,
              entityCode: undefined,
            }))
          }
          allowClear
        />
      </Col>

      <Col span={24} md={12} lg={6}>
        <CpfCnpjInput
          placeholder="CPF | CNPJ"
          value={queryParams?.cpfCnpj}
          onChange={(value) =>
            setQueryParams((prev) => ({
              ...prev,
              cpfCnpj: removeCpfCnpjMask(value),
            }))
          }
        />
      </Col>

      <Col span={24} md={12} lg={6}>
        {queryParams?.entity === InvoiceOriginReferenceEntity.PURCHASE && (
          <Input
            placeholder="Compra"
            value={queryParams?.entityCode?.join(', ')}
            onChange={({ target }) =>
              setQueryParams((prev) => ({
                ...prev,
                entityCode: target.value.split(',').map((v) => v.trim()),
              }))
            }
          />
        )}

        {queryParams?.entity === InvoiceOriginReferenceEntity.SALES && (
          <Input
            placeholder="Venda"
            value={queryParams?.entityCode?.join(', ')}
            onChange={({ target }) =>
              setQueryParams((prev) => ({
                ...prev,
                entityCode: target.value.split(',').map((v) => v.trim()),
              }))
            }
          />
        )}

        {queryParams?.entity === InvoiceOriginReferenceEntity.WORK_ORDER && (
          <Input
            placeholder="Orçamento"
            value={queryParams?.entityCode?.join(', ')}
            onChange={({ target }) =>
              setQueryParams((prev) => ({
                ...prev,
                entityCode: target.value.split(',').map((v) => v.trim()),
              }))
            }
          />
        )}
      </Col>
    </Row>
  );
}

export default InvoiceExtraFilters;
